import React, { useState, useEffect } from "react";
import { TablePagination, Tooltip } from "@mui/material";
import ALoader from "views/pages/manage/common/a-loader";

import {
  issueStockPaginationStyle,
  pageDropdownStyles,
} from "../../edpOps/stockManagement/selectMeterialAndStock/constant";
import { GetNextPrevIcon } from "../../edpOps/stockManagement/selectMeterialAndStock/issueStock/IssueStockTable";
import NoRowOverlay from "../../masters/holidayMaster/NoRowOverlay";
import styles from "./styles.module.scss";
import { ReactComponent as Sort } from "../../../../../assets/img/svg/sort.svg";
import { ReactComponent as SortDesc } from "../../../../../assets/img/svg/sortDesc.svg";

const FlexiTable = ({
  columns,
  rows,
  totalRows,
  page,
  onPageChange,
  onPageSizeChange,
  loading = false,
  pageSizeOptions = [50, 100, 150, 200, 250],
  totalPage = 0,
  enableSorting = true,
  showHeader = true,
  onRowClick,
}) => {
  const [rowsPerPage, setRowsPerPage] = useState(pageSizeOptions[0]);
  const [sortedRows, setSortedRows] = useState(rows);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedRow, setSelectedRow] = useState(null);

  useEffect(() => {
    if (sortBy) {
      const sortedArray = [...rows];
      sortedArray.sort((a, b) => {
        const value1 = String(a[sortBy] || "").toUpperCase();
        const value2 = String(b[sortBy] || "").toUpperCase();
        if (value1 < value2) return sortOrder === "asc" ? -1 : 1;
        if (value1 > value2) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
      setSortedRows(sortedArray);
    } else {
      setSortedRows(rows);
    }
  }, [rows, sortBy, sortOrder]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const handleRowClick = (row) => {
    setSelectedRow(row.id || row.applicationId || row.psid);
    if (onRowClick) onRowClick(row);
  };

  const handlePageSizeChange = (event) => {
    const newSize = Number(event.target.value);
    setRowsPerPage(newSize);
    onPageSizeChange(newSize);
  };

  const gridTemplateColumns = columns
    .map((col) => col.width || `minmax(100px, 1fr)`)
    .join(" ");

  return (
    <div>
      <div style={{ maxHeight: "500px", overflowY: "auto" }}>
        {showHeader && (
          <div
            className={`${styles["custom-table-parent"]} ${styles["header-column"]}`}
            style={{
              display: "grid",
              gridTemplateColumns,
              gridGap: "15px",
              alignItems: "center",
              marginBottom: "8px",
              position: "sticky",
              top: 0,
              background: "#fff",
              zIndex: 1,
            }}
          >
            {columns.map((col) => (
              <div key={col.field} style={col.style || {}}>
                {col.tooltip ? (
                  <Tooltip title={col.tooltip} placement="top">
                    <span>{col.headerName}</span>
                  </Tooltip>
                ) : (
                  col.headerName
                )}
                {enableSorting && col.sortable !== false && (
                  <span
                    className={`ml-1 ${styles.pointer}`}
                    onClick={() => handleSort(col.field)}
                  >
                    {sortBy === col.field ? (
                      sortOrder === "asc" ? (
                        <Sort />
                      ) : (
                        <SortDesc />
                      )
                    ) : (
                      " ⇅"
                    )}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        <div
          style={{
            maxHeight: "320px",
            overflow: "scroll",
            overflowX: "hidden",
          }}
        >
          {loading && <ALoader />}
          {sortedRows.length > 0 ? (
            sortedRows.map((row, index) => {
              const rowId = row.id || row.applicationId || row.psid || index;
              const isSelected = selectedRow === rowId;
              return (
                <div
                  key={rowId}
                  className={`${styles["custom-table-parent"]} ${
                    styles["table-body"]
                  } ${isSelected ? styles["selected-row"] : ""}`}
                  onClick={() => handleRowClick(row)}
                  style={{
                    display: "grid",
                    gridTemplateColumns,
                    gridGap: "15px",
                    alignItems: "center",
                    padding: "10px 0",
                    backgroundColor: isSelected ? "#f5f5f5" : "transparent",
                  }}
                >
                  {columns.map((col) => (
                    <div key={col.field} style={col.style || {}}>
                      {col.render ? col.render(row) : row[col.field]}
                    </div>
                  ))}
                </div>
              );
            })
          ) : (
            <NoRowOverlay />
          )}
        </div>
      </div>

      <TablePagination
        component="div"
        page={page}
        count={totalRows}
        rowsPerPage={rowsPerPage}
        onPageChange={(e, newPage) => onPageChange(newPage, rowsPerPage)}
        rowsPerPageOptions={pageSizeOptions}
        onRowsPerPageChange={handlePageSizeChange}
        sx={issueStockPaginationStyle}
        ActionsComponent={() => (
          <div
            className="d-flex gap-md"
            style={{ fontFamily: "Roboto, Helvetica, Arial, sans-serif" }}
          >
            <select
              style={pageDropdownStyles}
              value={page}
              onChange={(e) =>
                onPageChange(Number(e.target.value), rowsPerPage)
              }
            >
              {[...Array(totalPage).keys()].map((pageNumber) => (
                <option key={pageNumber} value={pageNumber}>
                  {pageNumber + 1}
                </option>
              ))}
            </select>
            <span>of {totalPage} pages</span>
            <span className="cursor d-flex gap-md align-items-center">
              <GetNextPrevIcon
                isDisabled={page === 0}
                onClickHandler={() => onPageChange(page - 1, rowsPerPage)}
              />
              <GetNextPrevIcon
                type="next"
                isDisabled={page + 1 === totalPage}
                onClickHandler={() => onPageChange(page + 1, rowsPerPage)}
              />
            </span>
          </div>
        )}
      />
    </div>
  );
};

export default FlexiTable;
